import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router';
import { ChevronLeft, Copy } from 'lucide-react';
import {
  can,
  LAYOUT_EDGES,
  MAX_GRID_SIDE,
  nextNumberOf,
  type LayoutEdge,
  type SessionMembership,
  type VenueSettings,
} from '@kratispot/shared';
import { Toasts, useToasts } from '@/components/toasts';
import { Button } from '@/components/ui/button';
import {
  ApiError,
  copyVenueLayout,
  fetchVenueLayout,
  fetchVenueSettings,
  saveVenueLayout,
} from '@/lib/api';
import { CopyLayoutDialog } from '@/components/map/copy-layout-dialog';
import {
  LAYOUT_TOOLS,
  cellAt,
  paint,
  resized,
  saveableDraft,
  unzonedSpots,
  type EditorDraft,
  type LayoutTool,
} from '@/lib/layout-edit';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { differs, forgetDraft, keepDraft, storedDraft, type StoredDraft } from '@/lib/layout-draft';
import { cn } from '@/lib/utils';
import { editedAt, wholeOf } from '@/lib/format';

const TOOL_COPY: Readonly<Record<LayoutTool, CopyKey>> = {
  spot: 'toolSpot',
  zone: 'toolZone',
  deck: 'toolDeck',
  bar: 'toolBar',
  kitchen: 'toolKitchen',
  wall: 'toolWall',
  erase: 'toolErase',
};

// The editor's own palette: the map's colours belong to statuses, not elements.
const TOOL_TONE: Readonly<Record<LayoutTool, string>> = {
  spot: 'bg-emerald-500',
  zone: 'bg-indigo-500',
  deck: 'bg-amber-600',
  bar: 'bg-stone-700',
  kitchen: 'bg-rose-700',
  wall: 'bg-slate-500',
  erase: 'bg-muted',
};

const CELL_TONE: Readonly<Record<string, string>> = {
  spot: 'bg-emerald-100 text-emerald-900 border-emerald-400',
  deck: 'bg-amber-200 border-amber-500',
  bar: 'bg-stone-300 border-stone-600',
  kitchen: 'bg-rose-200 border-rose-500',
  wall: 'bg-slate-300 border-slate-500',
};

// One tint per zone, repeating; none green, which a zoneless spot wears. §18
const ZONE_TONES = [
  'bg-sky-100 text-sky-900 border-sky-400',
  'bg-violet-100 text-violet-900 border-violet-400',
  'bg-amber-100 text-amber-900 border-amber-500',
  'bg-rose-100 text-rose-900 border-rose-400',
  'bg-cyan-100 text-cyan-900 border-cyan-400',
  'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-400',
  'bg-orange-100 text-orange-900 border-orange-400',
  'bg-indigo-100 text-indigo-900 border-indigo-400',
] as const;

const EDGE_COPY: Readonly<Record<LayoutEdge, CopyKey>> = { sea: 'edgeSea' };

// A spot the venue no longer zones keeps the plain spot tone rather than none.
export function zoneTone(index: number): string {
  return index < 0 ? (CELL_TONE.spot as string) : (ZONE_TONES[index % ZONE_TONES.length] as string);
}

const NUMBER_INPUT = 'w-20 rounded-lg border px-2 py-1 text-end text-base';

// A zone reads in the language on screen, then the venue's own, then its key. §6
export function zoneLabel(
  zone: VenueSettings['zones'][number],
  language: string,
  fallback: string,
): string {
  return zone.names[language] ?? zone.names[fallback] ?? zone.key;
}

// §18: a floor is copied from another the caller may draw, never one they see.
export function drawableOthers(
  venues: readonly SessionMembership[],
  venueId: string,
): SessionMembership[] {
  return venues.filter((venue) => venue.venueId !== venueId && can(venue, 'venue:layout'));
}

// Paint spot / decking / bar / erase on a grid; saving rebuilds the map. §18
export function LayoutPage({
  venueId,
  venues = [],
}: {
  venueId: string;
  venues?: readonly SessionMembership[];
}): ReactElement {
  const { t, language } = useTranslation();
  const [settings, setSettings] = useState<VenueSettings | null>(null);
  const [draft, setDraft] = useState<EditorDraft | null>(null);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [tool, setTool] = useState<LayoutTool>('spot');
  const [zoneKey, setZoneKey] = useState<string | null>(null);
  const [next, setNext] = useState(1);
  const [busy, setBusy] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  // §9: the numbers a refused save named, so the floor shows which they are.
  const [unzoned, setUnzoned] = useState<readonly number[]>([]);
  // What the API holds, so an edit can tell itself from it. §18
  const [saved, setSaved] = useState<EditorDraft | null>(null);
  const [resumable, setResumable] = useState<StoredDraft | null>(null);
  const { toasts, push } = useToasts();

  const load = useCallback(
    (signal?: AbortSignal) =>
      Promise.all([fetchVenueLayout(venueId, signal), fetchVenueSettings(venueId, signal)])
        .then(([loaded, config]) => {
          // Read before anything is drawn: a draft is offered, never applied. §18
          const kept = storedDraft(venueId);
          setResumable(kept !== null && differs(kept.draft, loaded) ? kept : null);
          setDraft(loaded);
          setSaved(loaded);
          setSettings(config);
          setNext(nextNumberOf(loaded.spots));
          setZoneKey(config.zones[0]?.key ?? null);
          setPhase('ready');
        })
        .catch(() => {
          if (!signal?.aborted) setPhase('failed');
        }),
    [venueId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Every change, so a closed tab costs nothing; cleared by a save or Discard.
  useEffect(() => {
    if (draft === null || saved === null || !differs(draft, saved)) return;
    keepDraft(venueId, draft, new Date().toISOString());
  }, [draft, saved, venueId]);

  if (phase !== 'ready' || draft === null || settings === null)
    return (
      <p
        role={phase === 'failed' ? 'alert' : undefined}
        className="p-6 text-center text-sm text-muted-foreground"
      >
        {t(phase === 'failed' ? 'errorGeneric' : 'loading')}
      </p>
    );

  // Narrowed once, so the handlers below never re-check what the guard proved.
  const floor: EditorDraft = draft;
  // A venue with no zone cannot price a spot, so the brush waits for one. §9
  const zones = settings.zones;
  // A floor already drawn is redrawn here, so the copy is offered to none. §18
  const others = drawableOthers(venues, venueId);
  const copyable = draft.spots.length === 0 && others.length > 0;

  function brush(row: number, col: number): void {
    if (zoneKey === null) return;
    const done = paint(floor, row, col, tool, next, zoneKey);
    setDraft(done.draft);
    setUnzoned(unzoned.filter((number) => unzonedSpots(done.draft).includes(number)));
    if (done.used) setNext(next + 1);
  }

  // §18: a floor already drawn is redrawn here, so the copy is offered to none.
  async function copyFrom(fromVenueId: string): Promise<void> {
    setBusy(true);
    setCopyError(null);
    try {
      const copied = await copyVenueLayout(venueId, fromVenueId);
      setDraft(copied);
      setNext(nextNumberOf(copied.spots));
      setCopying(false);
      push(t('copyLayoutDone'), 'bg-emerald-600');
    } catch (error) {
      const occupied = error instanceof ApiError && error.status === 409;
      setCopyError(t(occupied ? 'copyLayoutOccupied' : 'copyLayoutFailed'));
    } finally {
      setBusy(false);
    }
  }

  function resize(rows: number, cols: number): void {
    setDraft(resized(floor, Math.min(rows, MAX_GRID_SIDE), Math.min(cols, MAX_GRID_SIDE)));
  }

  async function save(): Promise<void> {
    // §9: a spot nobody zoned cannot be priced, so the save never leaves here.
    const ready = saveableDraft(floor);
    if (ready === null) {
      const waiting = unzonedSpots(floor);
      setUnzoned(waiting);
      push(`${t('layoutUnzoned')} ${waiting.join(', ')}`, 'bg-red-500');
      return;
    }
    setBusy(true);
    try {
      const landed = await saveVenueLayout(venueId, ready);
      setDraft(landed);
      setSaved(landed);
      setUnzoned([]);
      setResumable(null);
      forgetDraft(venueId);
      setNext(nextNumberOf(landed.spots));
      push(t('layoutSaved'), 'bg-emerald-600');
    } catch (error) {
      // The server names the spots and bookings in the way; say exactly that. §18
      const said = error instanceof ApiError ? error.detail : null;
      push(said ?? t('layoutFailed'), 'bg-red-500');
    } finally {
      setBusy(false);
    }
  }

  const zoneIndexOf = (key: string) => zones.findIndex((zone) => zone.key === key);
  const zoneNameOf = (key: string) => {
    const zone = zones[zoneIndexOf(key)];
    return zone === undefined ? key : zoneLabel(zone, language, settings.defaultLanguage);
  };

  const rows = Array.from({ length: draft.gridRows }, (_, index) => index + 1);
  const cols = Array.from({ length: draft.gridCols }, (_, index) => index + 1);

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to="/settings">
            <ChevronLeft className="size-4" aria-hidden />
            {t('back')}
          </Link>
        </Button>
        <h2 className="truncate text-base font-bold">{t('layoutEditor')}</h2>
        {copyable && (
          <Button
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={() => {
              setCopyError(null);
              setCopying(true);
            }}
          >
            <Copy className="size-4" aria-hidden />
            {t('copyLayout')}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-card p-2">
        {LAYOUT_TOOLS.map((each) => (
          <Button
            key={each}
            size="sm"
            variant={tool === each ? 'default' : 'outline'}
            aria-pressed={tool === each}
            onClick={() => setTool(each)}
          >
            <i className={cn('size-3 rounded-sm border', TOOL_TONE[each])} aria-hidden />
            {t(TOOL_COPY[each])}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-2 text-sm">
        <label className="flex items-center gap-1.5">
          {t('nextNumber')}
          <input
            type="number"
            min={1}
            className={NUMBER_INPUT}
            aria-label={t('nextNumber')}
            value={next}
            onChange={(event) => setNext(Math.max(1, wholeOf(event.target.value)))}
          />
        </label>
        <label className="flex items-center gap-1.5">
          {t('zoneForSpots')}
          <select
            className="rounded-lg border px-2 py-1 text-base"
            aria-label={t('zoneForSpots')}
            disabled={zones.length === 0}
            value={zoneKey ?? ''}
            onChange={(event) => setZoneKey(event.target.value)}
          >
            {zones.map((zone) => (
              <option key={zone.key} value={zone.key}>
                {zoneLabel(zone, language, settings.defaultLanguage)}
              </option>
            ))}
          </select>
        </label>
        {/* One group, never wrapped: a grid reads "Rows n × Columns m". §18 */}
        <div data-testid="grid-size" className="flex flex-nowrap items-center gap-1.5">
          <label className="flex items-center gap-1.5">
            {t('gridRows')}
            <input
              type="number"
              min={1}
              max={MAX_GRID_SIDE}
              className={NUMBER_INPUT}
              aria-label={t('gridRows')}
              value={draft.gridRows}
              onChange={(event) => resize(Math.max(1, wholeOf(event.target.value)), draft.gridCols)}
            />
          </label>
          <span aria-hidden>×</span>
          <label className="flex items-center gap-1.5">
            {t('gridCols')}
            <input
              type="number"
              min={1}
              max={MAX_GRID_SIDE}
              className={NUMBER_INPUT}
              aria-label={t('gridCols')}
              value={draft.gridCols}
              onChange={(event) => resize(draft.gridRows, Math.max(1, wholeOf(event.target.value)))}
            />
          </label>
        </div>
        <label className="flex items-center gap-1.5">
          {t('frontEdge')}
          <select
            className="rounded-lg border px-2 py-1 text-base"
            aria-label={t('frontEdge')}
            value={draft.frontEdge ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, frontEdge: (event.target.value || null) as LayoutEdge | null })
            }
          >
            <option value="">{t('edgeNone')}</option>
            {LAYOUT_EDGES.map((edge) => (
              <option key={edge} value={edge}>
                {t(EDGE_COPY[edge])}
              </option>
            ))}
          </select>
        </label>
      </div>

      <ul
        data-testid="zone-legend"
        className="flex flex-wrap items-center gap-3 rounded-xl border bg-card px-2 py-1.5 text-sm"
      >
        {zones.map((zone, index) => (
          <li key={zone.key} className="flex items-center gap-1">
            <i className={cn('size-3 rounded-sm border', zoneTone(index))} aria-hidden />
            {zoneLabel(zone, language, settings.defaultLanguage)}
          </li>
        ))}
        <li className="flex items-center gap-1 text-muted-foreground">
          <i className={cn('size-3 rounded-sm border', zoneTone(-1))} aria-hidden />
          {t('zoneGone')}
        </li>
      </ul>

      {zones.length === 0 && (
        <p role="alert" className="rounded-xl border bg-card p-3 text-sm text-amber-700">
          {t('layoutNeedsZone')}
        </p>
      )}

      {resumable !== null && (
        <p
          role="status"
          className="flex flex-wrap items-center gap-2 rounded-xl border bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <span className="flex-1">
            {t('unsavedDraft')} {editedAt(resumable.at, language)}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setDraft(resumable.draft);
              setNext(nextNumberOf(resumable.draft.spots));
              setResumable(null);
            }}
          >
            {t('resumeDraft')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              forgetDraft(venueId);
              setResumable(null);
            }}
          >
            {t('discardDraft')}
          </Button>
        </p>
      )}

      <div className="overflow-x-auto rounded-xl border bg-card p-3">
        <div className="w-max">
          {draft.frontEdge !== null && (
            <div
              data-testid="editor-edge"
              className="mb-1 rounded-t bg-linear-to-b from-sky-300 to-sky-50 py-1 text-center text-[10px] font-bold tracking-[0.35em] text-sky-900"
            >
              {t('sea')}
            </div>
          )}
          <div
            data-testid="editor-grid"
            className="grid w-max gap-[3px]"
            style={{ gridTemplateColumns: `repeat(${draft.gridCols}, 30px)` }}
          >
            {rows.map((row) =>
              cols.map((col) => {
                const cell = cellAt(draft, row, col);
                const spot = cell?.kind === 'spot' ? cell.spot : null;
                const zoned = spot?.zoneKey ?? null;
                // §9: a refused save names the spots nobody priced; here they are.
                const blocking = spot !== null && unzoned.includes(spot.number);
                return (
                  <button
                    key={`${row}:${col}`}
                    type="button"
                    data-testid={`cell-${row}-${col}`}
                    data-kind={cell?.kind ?? 'empty'}
                    data-zone={zoned ?? (spot === null ? undefined : 'none')}
                    title={
                      zoned === null
                        ? spot === null
                          ? undefined
                          : t('zoneGone')
                        : zoneNameOf(zoned)
                    }
                    aria-label={`${row}:${col}`}
                    className={cn(
                      'h-[30px] rounded-[5px] border text-[10px] font-extrabold',
                      cell === null && 'border-dashed bg-muted/40',
                      cell !== null && cell.kind !== 'spot' && CELL_TONE[cell.kind],
                      spot !== null && zoneTone(zoneIndexOf(zoned ?? '')),
                      blocking && 'border-2 border-red-600 ring-2 ring-red-500/60',
                    )}
                    onPointerDown={() => brush(row, col)}
                    onPointerEnter={(event) => {
                      if (event.buttons === 1) brush(row, col);
                    }}
                  >
                    {cell?.kind === 'spot' ? cell.spot.number : ''}
                  </button>
                );
              }),
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{t('layoutHint')}</p>

      <div className="flex flex-wrap items-center gap-3">
        <Button disabled={busy} onClick={() => void save()}>
          {t('saveLayout')}
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            forgetDraft(venueId);
            setResumable(null);
            void load();
          }}
        >
          {t('discard')}
        </Button>
      </div>
      {copying && (
        <CopyLayoutDialog
          venueName={venues.find((venue) => venue.venueId === venueId)?.venueName ?? ''}
          others={others}
          busy={busy}
          error={copyError}
          onClose={() => {
            setCopying(false);
            setCopyError(null);
          }}
          onCopy={(fromVenueId) => void copyFrom(fromVenueId)}
        />
      )}
      <Toasts toasts={toasts} undoLabel={t('undo')} />
    </section>
  );
}
